import type { Meter } from '@audigoal/core';
import { Scheduler, type Clock, type Ticker } from './scheduler';
import type { PercussionSynth } from './ui-synth';
import { secondsPerBeat } from './timing';

export interface MetronomeOptions {
  bpm: number;
  meter: Meter;
  /** Accent beat 1 (and the dotted-quarter beats in compound time). */
  accent?: boolean;
  /** Beats of count-in before bar 1; callbacks receive negative beats. */
  countIn?: number;
  /** Extra ticks on the eighth subdivisions. */
  subdivide?: boolean;
  /** Stop after this many bars; runs until stopped when undefined. */
  bars?: number;
}

export type BeatCallback = (beat: number, bar: number, time: number) => void;

/**
 * Metronome that schedules a bar at a time, so tempo changes and stops take
 * effect within one bar. Beats are in quarter notes for simple meters and
 * dotted quarters for compound ones (6/8 ticks twice per bar).
 */
export class Metronome {
  private scheduler: Scheduler | null = null;
  private listeners = new Set<BeatCallback>();
  private running = false;
  private options: MetronomeOptions | null = null;

  constructor(
    private readonly clock: Clock,
    private readonly synth: PercussionSynth,
    private readonly makeTicker?: () => Ticker,
  ) {}

  get isRunning(): boolean {
    return this.running;
  }

  onBeat(callback: BeatCallback): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  start(options: MetronomeOptions, startAt = this.clock.now() + 0.05): void {
    this.stop();
    this.options = options;
    this.running = true;
    const compound = options.meter.unit === 8 && options.meter.beats % 3 === 0;
    const beatsPerBar = compound ? options.meter.beats / 3 : options.meter.beats;
    const spb = compound ? secondsPerBeat(options.bpm) * 1.5 : secondsPerBeat(options.bpm);
    const countIn = options.countIn ?? 0;
    const totalBars = options.bars;

    const events = [];
    let time = startAt;
    for (let i = 0; i < countIn; i += 1) {
      const beat = i - countIn;
      const t = time;
      events.push({
        time: t,
        fire: (at: number) => this.tick(at, beat, -1, i === 0 && options.accent !== false),
      });
      time += spb;
    }
    const bars = totalBars ?? 64;
    for (let bar = 0; bar < bars; bar += 1) {
      for (let beat = 0; beat < beatsPerBar; beat += 1) {
        const t = time;
        const accent = beat === 0 && options.accent !== false;
        events.push({ time: t, fire: (at: number) => this.tick(at, beat, bar, accent) });
        if (options.subdivide === true) {
          const subs = compound ? 3 : 2;
          for (let s = 1; s < subs; s += 1) {
            const st = t + (spb * s) / subs;
            events.push({ time: st, fire: (at: number) => this.synth.click(at, 'subdivision') });
          }
        }
        time += spb;
      }
    }
    this.scheduler = new Scheduler(this.clock, {
      ...(this.makeTicker !== undefined ? { ticker: this.makeTicker() } : {}),
      onDone: () => {
        this.running = false;
      },
    });
    this.scheduler.add(events, time);
    this.scheduler.start();
  }

  stop(): void {
    this.scheduler?.stop();
    this.scheduler = null;
    this.running = false;
  }

  private tick(at: number, beat: number, bar: number, accent: boolean): void {
    this.synth.click(at, accent ? 'accent' : 'normal');
    for (const listener of this.listeners) listener(beat, bar, at);
  }

  get current(): MetronomeOptions | null {
    return this.options;
  }
}
